import { Route, Navigate, Routes, useNavigate } from "react-router";
import { useShallow } from "zustand/shallow";
import { AnimatePresence } from "framer-motion";
import { useEffect } from "react";

import { useBoundStore } from "@/app/store";
import { loadKeyFromDB } from "@/features/encryption/utils/storage";
import { NotesRoute } from "./notes/NotesRoute";
import { NotesTrashRoute } from "./notes/NotesTrashRoute";
import { SettingsRoute } from "./settings/SettingsRoute";
import { HelpRoute } from "./settings/HelpRoute";
import { AppInfoRoute } from "./settings/AppInfoRoute";
import { FeedbackRoute } from "./feedback/FeedbackRoute";
import { NotesCreateRoute } from "./notes/NoteCreateRoute";
import { NoteEditRoute } from "./notes/NoteEditRoute";
import { NoteSettingsRoute } from "./notes/NoteSettingsRoute";
import { NotesTrashNoteRoute } from "./notes/NotesTrashNoteRoute";
import { TagsRoute } from "./tags/TagsRoute";
import { TodosRoute } from "./todos/TodosRoute";
import { ShareNoteRoute } from "../public/share/ShareNoteRoute";
import { NoteSettingsToolbarRoute } from "./notes/NoteSettingsToolbarRoute";
import { SettingsProfileRoute } from "./settings/SettingsProfileRoute";
import { OnboardingRoute } from "./onboarding/OnboardingRoute";
import { HomeRoute } from "./home/HomeRoute";
import { PageLayout } from "@/shared/components/layout/PageLayout";
import { UnlockDB } from "./unlock/UnlockDB";

export function PrivateRoutes() {
  const deviceType = useBoundStore(useShallow((state) => state.device.type));
  const isAuthenticated = useBoundStore(useShallow((state) => state.user.isAuthenticated));
  const isAnonymous = useBoundStore(useShallow((state) => state.user.isAnonymous));
  const dbLocked = useBoundStore(useShallow((state) => state.user.dbLocked));
  const fetchSettings = useBoundStore((state) => state.settings.fetchSettings);
  const updateSettings = useBoundStore((state) => state.settings.updateSettings);
  const setUser = useBoundStore((state) => state.user.set);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated || dbLocked) {
      return;
    }

    (async () => {
      // Fetch settings first to get actual server settings
      const userSettings = await fetchSettings();
      const dek = await loadKeyFromDB();

      if (!userSettings.app.onboardingDone) {
        if (isAnonymous) {
          const currentUserId = useBoundStore.getState().user.id;
          if (currentUserId) {
            await updateSettings(currentUserId, {
              app: { ...userSettings.app, onboardingDone: true },
            });
          }
          return;
        }
        navigate("/onboarding", { replace: true });
        return;
      }

      if (!dek && userSettings.app.encryption) {
        setUser({ dbLocked: true });
        navigate("/unlock", { replace: true });
        return;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, dbLocked, isAnonymous]);

  return (
    <AnimatePresence mode="wait">
      <Routes>
        <Route element={<PageLayout />}>
          <Route path="/share/notes/:noteUuid" element={<ShareNoteRoute />} />
          <Route path="/unlock" element={<UnlockDB />} />
          <Route path="/tags" element={<TagsRoute />} />
          <Route path="todos" element={<TodosRoute />} />
          <Route path="settings">
            <Route index element={<SettingsRoute />} />
            <Route path="profile" element={<SettingsProfileRoute />} />
            <Route path="help">
              <Route index element={<HelpRoute />} />
              <Route path="app-info" element={<AppInfoRoute />} />
            </Route>
            <Route path="feedback" element={<FeedbackRoute />} />
          </Route>
          <Route path="home" element={<HomeRoute />} />
          <Route path="onboarding" element={<OnboardingRoute />} />
          <Route path="notes">
            <Route index element={<Navigate to="/notes/0" replace />} />
            {deviceType === "mobile" && <Route path="tags" element={<TagsRoute />} />}
            <Route path="trash">
              <Route index element={<NotesTrashRoute />} />
              <Route path=":id" element={<NotesTrashNoteRoute />} />
            </Route>
            <Route path="create" element={<NotesCreateRoute />} />
            <Route path=":folderId">
              <Route index element={<NotesRoute />} />
              <Route path="create" element={<NotesCreateRoute />} />
              <Route path="create/settings">
                <Route index element={<NoteSettingsRoute />} />
                <Route path="toolbar" element={<NoteSettingsToolbarRoute />} />
              </Route>
              <Route path=":noteId" element={<NoteEditRoute />} />
              <Route path=":noteId/settings">
                <Route index element={<NoteSettingsRoute />} />
                <Route path="toolbar" element={<NoteSettingsToolbarRoute />} />
              </Route>
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/home" replace />} />
        </Route>
      </Routes>
    </AnimatePresence>
  );
}
